import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core.IF.BO'
import {IProgressProvider} from '../PA.Core.IF.Basic'
import {ProgressProvider} from '../Basic/Progress'
import {IRecordResolverProvider, implementsIRecordResolverProvider} from "../PA.Core.IF.BO";
import {ASync} from "../Basic/ASync";




export class BusinessObjectLoader<aBO extends IBusinessObject> {
  _aBOFactory: { new(opts?: any): aBO };
  LoadProgressProvider: IProgressProvider;
  _createOpts: any;
  _loadIds: (string | number) []
  _workerCount: number
  _loadQueueIds: (string | number)[];
  _ConcurrentLoadCount: number;
  _loadSuccessCount: number;
  _loadErrorCount: number;
  _loadErrorExceptions: any[];
  _loadedBOs: aBO[];

  _bUseRecordResolver: boolean
  _IRecordResolverProvider: IRecordResolverProvider

  _cbOnEachLoadComplete: (aBO: IBusinessObject) => void
  _cbOnEachLoadFailed: (anId: (string | number)) => void

  constructor(pBOFactory: { new(opts: any): aBO }, createOpts: any, arrIDs: (string | number)[], workerCount?: number) {

    this._loadIds = arrIDs;
    this._workerCount =  Math.max(workerCount || 1, 1)
    this._loadQueueIds = arrIDs.slice(); // clone
    this._createOpts = createOpts || {};
    this._aBOFactory = pBOFactory;
    this.LoadProgressProvider = new ProgressProvider("Business Object Loader");

    this._ConcurrentLoadCount = 1;
    this._loadSuccessCount = 0;
    this._loadErrorCount = 0;
    this._loadErrorExceptions = [];
    this._loadedBOs = [];
    this._bUseRecordResolver = false
    this._IRecordResolverProvider = undefined

    if(implementsIRecordResolverProvider(this._aBOFactory)){
      this._IRecordResolverProvider = <any> this._aBOFactory
      this._bUseRecordResolver = true
      this._ConcurrentLoadCount = this._IRecordResolverProvider.concurrentLoadCountHint()
    }

  }

  get LoadedBOs(): aBO[] {
    return this._loadedBOs;
  }

  set cbOnEachLoadComplete(fnValue: (aBO: IBusinessObject) => void) {
    this._cbOnEachLoadComplete = fnValue
  }

  set cbOnEachLoadFailed(fnValue: (anId: (string | number)) => void) {
    this._cbOnEachLoadFailed = fnValue
  }


  async Load(pLoadOptions: any): Promise<BusinessObjectLoader<aBO>> {
    const self = this;
    let loadOpts = pLoadOptions || {};

    let workerTasks: Promise<void> [] = []

    try {
      self.LoadProgressProvider.start("Starting data load");
      for (let workerIdx = 0; workerIdx < self._workerCount; workerIdx++) {
        workerTasks.push(self._SubLoad(loadOpts))
      }
      await Promise.all(workerTasks)

      self.updateProgress();
      if (self._loadErrorCount == 0) {
        self.LoadProgressProvider.finish();
        return self
      } else {
        self.LoadProgressProvider.exception(self._loadErrorExceptions);
        return self
      }
    } catch (err) {
      self.updateProgress();
      self.LoadProgressProvider.exception(err);
    }

    return self
  }

  async _SubLoad(loadOptions: any): Promise<void> {
    const self = this;

    if (self._loadQueueIds.length == 0)
      return


    let arrSubLoadIds: any[] = []
    for (let i = 0; i < self._ConcurrentLoadCount; i++) {
      let loadId: any;
      if (self._loadQueueIds.length == 0)
        break;
      else
        loadId = self._loadQueueIds.pop();
      arrSubLoadIds.push(loadId)
    }

    // Setup RecordResolver, if available.
    if (self._bUseRecordResolver) {
      let recordResolver = self._IRecordResolverProvider.createRecordResolver(loadOptions)
      loadOptions.RecordResolver = recordResolver
      await self._IRecordResolverProvider.setupRecordResolver(recordResolver, arrSubLoadIds)
    }


    // Dequeue X ids and load them
    let tasks: Promise<any>[] = [];

    for (let i = 0; i < arrSubLoadIds.length; i++) {

      let loadId: any = arrSubLoadIds[i]

      let aBoToLoad = new self._aBOFactory(this._createOpts);
      self._loadedBOs.push(aBoToLoad);

      tasks.push(aBoToLoad.Load(loadId, loadOptions).then((returnBO) => {
        if (returnBO.bIsLoaded) {
          self._loadSuccessCount += 1;
          if (self._cbOnEachLoadComplete) {
            self._cbOnEachLoadComplete(returnBO)
          }
          self.updateProgress();
        } else {
          self._loadErrorCount += 1;
          if (self._cbOnEachLoadFailed) {
            self._cbOnEachLoadFailed(loadId)
          }
          self._loadErrorExceptions.push(returnBO.LoadProgressProvider.process_exception);
          self.updateProgress();
        }
      }).catch((err) => {
        self._loadErrorCount += 1;
        self._loadErrorExceptions.push(err);
        self.updateProgress();
      }));

    }

    if (tasks.length > 0) {
      await Promise.all(tasks)
      // if there is more, recurse.
      if (self._loadQueueIds.length > 0) {
        //-- Recurse
        await self._SubLoad(loadOptions)
      }
    }

  }

  updateProgress() {

    var total_count = this._loadIds.length;
    var in_progress_count = this._loadedBOs.length - this._loadSuccessCount - this._loadErrorCount;
    var waiting_in_queue = total_count - this._loadedBOs.length;
    var sStatusMessage = 'Total: ' + total_count +
      ', Success/Error: ' + this._loadSuccessCount + '/ ' + this._loadErrorCount +
      ', Processing/Waiting: ' + in_progress_count + '/ ' + waiting_in_queue;

    this.LoadProgressProvider.setProgressMessage('BusinessObjectLoader.status', sStatusMessage);
    if (total_count > 0) {
      this.LoadProgressProvider.setProgressDenominator(total_count)
      this.LoadProgressProvider.setProgressNumerator(this._loadSuccessCount + this._loadErrorCount)
    }
  }
}

